import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { BrainIcon, ZapIcon, ShieldCheckIcon, EyeIcon, ActivityIcon, ScaleIcon, CheckCircle2Icon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { InteractiveTerminal } from './InteractiveTerminal';
export function PricingSection() {
  const revealRef = useScrollReveal();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setAnimated(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    const bars = document.getElementById('pricing-progress-bars');
    if (bars) observer.observe(bars);
    return () => observer.disconnect();
  }, []);
  const progressBars = [{
    label: 'Presnosť',
    value: '97%',
    width: '97%'
  }, {
    label: 'Rýchlosť',
    value: '10x',
    width: '85%'
  }, {
    label: 'Pokrytie',
    value: '94%',
    width: '94%'
  }];
  const nodeCards = [{
    title: 'OmniMem',
    description: 'Digitálna pamäť vašej firmy. AI spojí e-maily, poznámky z meetingov a dokumenty do jedného celku — nájdete čokoľvek za sekundu.',
    icon: <BrainIcon className="w-6 h-6 text-apple-blue" data-id="element-456" data-mp="17pxn64038chr" />,
    gradient: 'from-blue-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'VibeStyle AI',
    description: 'Vytvorte vizuály, ktoré sedia vašej značke. AI pozná váš štýl a automaticky prispôsobí každý výstup tak, aby vyzeral profesionálne.',
    icon: <EyeIcon className="w-6 h-6 text-purple-500" data-id="element-457" data-mp="k2pkltu1nhjf" />,
    gradient: 'from-purple-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'EchoVision Pro',
    description: 'Preklad reality pre nevidiacich. Priestorový zvuk vytvorený z obrazu scény — používateľ „počuje" prekážky, ľudí aj objekty okolo seba.',
    icon: <ZapIcon className="w-6 h-6 text-amber-500" data-id="element-458" data-mp="7j29f0aj554s" />,
    gradient: 'from-amber-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'SkillStream',
    description: 'Učenie remesiel cez kameru a hlas. AI sleduje vaše ruky a krok za krokom vám hovorí, čo robiť — ako keby vám majster stál za chrbtom.',
    icon: <ActivityIcon className="w-6 h-6 text-emerald-500" data-id="element-459" data-mp="3kwngo5trrwog" />,
    gradient: 'from-emerald-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'MediMatch',
    description: 'Rýchla zdravotná kontrola z fotky a zvuku. Odfotíte vyrážku, nahráte kašeľ — AI pomôže lekárovi s prvotným vyhodnotením na diaľku.',
    icon: <ShieldCheckIcon className="w-6 h-6 text-rose-500" data-id="element-460" data-mp="2scjtp4strlsq" />,
    gradient: 'from-rose-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=300&fit=crop&crop=center'
  }, {
    title: 'LawLens',
    description: 'Odfotíte zmluvu a spýtate sa: „Čo ak prestanem platiť?" AI vám jednoducho vysvetlí, čo to znamená — bez právnického žargónu.',
    icon: <ScaleIcon className="w-6 h-6 text-indigo-500" data-id="element-461" data-mp="3ba31y67gl0e3" />,
    gradient: 'from-indigo-50 to-transparent',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&h=300&fit=crop&crop=center'
  }];
  const compactItems = ['07. Diagnostika áut podľa zvuku motora', '08. Recepty z pohľadu do chladničky', '09. Sledovanie nálady podľa hlasu', '10. Preklad s porozumením gest a emócií', '11. Filmová hudba z hmkania melódie', '12. Domáca bezpečnosť, ktorá chápe situácie', '13. Hľadanie autentických miest podľa zvukov', '14. Analýza mimiky pri online predaji', '15. Nakresli appku na papier — AI ju vytvorí', '16. Automatické triedenie fotiek z eventov', '17. Vizuálna kontrola dodávateľského reťazca', '18. Diskusia s videoprednáškou v reálnom čase', '19. Skladový manažment cez fotoaparát', '20. Analýza pohybu pre športovcov'];
  return <section id="portfolio" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-462" data-mp="3v6pa6af3r0xm">
      <div className="max-w-7xl mx-auto" data-id="element-463" data-mp="806cmckmpg4s">
        {/* 1. SECTION HEADING */}
        <div className="reveal text-center mb-36" data-id="element-464" data-mp="829d5qjeqrnr">
          <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight text-apple-black" data-id="element-465" data-mp="d012qdwky1os">
            20 Produktov. Jeden Engine. Vaša Príležitosť.
          </h2>
        </div>

        {/* 2. GEMINI 2 HERO INTRO BLOCK */}
        <div className="reveal max-w-4xl mx-auto text-center mb-28" data-id="element-466" data-mp="1ocbo3z8zuvj7">
          <p className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-8" data-id="element-467" data-mp="38v5of6cbf5xv">
            AI Platforma Novej Generácie
          </p>
          <h3 className="text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black mb-8 leading-tight" data-id="element-468" data-mp="33zgnozok7izz">
            Čo všetko dokážeme postaviť
          </h3>
          <p className="text-lg md:text-xl text-apple-gray leading-relaxed max-w-3xl mx-auto mb-10 font-light" data-id="element-469" data-mp="2u6rnj0yglrjy">
            Každý z týchto 20 produktov je reálny projekt, ktorý môžeme dodať.
            Nie teória, nie koncept — fungujúce PWA aplikácie postavené na
            Gemini 2 engine. Vyberte si ten, ktorý rieši váš problém, alebo nám
            povedzte svoj nápad.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-470" data-mp="2b9qf165p59b9">
            <div className="bg-apple-blue/10 text-apple-blue border border-apple-blue/30 rounded-full px-6 py-2.5 font-semibold text-sm shadow-[0_0_25px_rgba(0,113,227,0.15)]" data-id="element-471" data-mp="egon30pbls1n">
              PWA Ready
            </div>
            <div className="bg-apple-blue/10 text-apple-blue border border-apple-blue/30 rounded-full px-6 py-2.5 font-semibold text-sm shadow-[0_0_25px_rgba(0,113,227,0.15)]" data-id="element-472" data-mp="23w93gv4g4y1q">
              20 Hotových Konceptov
            </div>
          </div>
        </div>

        {/* 3. ANIMATED PROGRESS BARS */}
        <div id="pricing-progress-bars" className="reveal max-w-3xl mx-auto mb-36 space-y-8" data-id="element-473" data-mp="1yz8xek7ljvam">
          {progressBars.map((bar, index) => <div key={index} className="w-full" data-id="element-474" data-mp="2rnek3ci7nud3" data-mpi={index}>
              <div className="flex justify-between items-end mb-3" data-id="element-475" data-mp="2rm3h3ewlaqn1">
                <span className="text-base font-semibold text-apple-black" data-id="element-476" data-mp="2dm245131vsr7">
                  {bar.label}
                </span>
                <span className="text-lg font-bold text-apple-blue" data-id="element-477" data-mp="2hmvs1ught7k3">
                  {bar.value}
                </span>
              </div>
              <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-478" data-mp="169a5mh20pk29">
                <div className="h-full bg-gradient-to-r from-apple-blue to-blue-400 rounded-full" style={{
              width: animated ? bar.width : '0%',
              transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.2}s`
            }} data-id="element-479" data-mp="yq0uai9ybbak" />
              </div>
            </div>)}
        </div>

        {/* 4. NESTAVIAME PROTOTYPY SECTION */}
        <div className="reveal mb-36" data-id="element-480" data-mp="g0ktruy9niu5">
          <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black text-center mb-16" data-id="element-481" data-mp="fxf3icdwiawu">
            Nestaviame prototypy. Staviame hotové riešenia.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-482" data-mp="pqnihiycuamo">
            <Card className="border-gray-200 rounded-3xl bg-apple-light/50 border-none shadow-sm card-hover-premium" data-id="element-483" data-mp="kxuhn660bmfv">
              <CardContent className="p-10" data-id="element-484" data-mp="3tcl2kzs2m7ao">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md mb-6" data-id="element-485" data-mp="3ofusqu6v07xp">
                  <ShieldCheckIcon className="w-7 h-7 text-apple-blue" data-id="element-486" data-mp="32x1muxzp64ys" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-487" data-mp="37ubac83j06jz">
                  Kvalita
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-488" data-mp="3ctegxwz5mku2">
                  Aplikácie, ktoré vyzerajú a fungujú bezchybne. Dostupnosť
                  99.99% znamená, že vaše riešenie beží spoľahlivo — aj keď ho
                  používajú tisíce ľudí naraz.
                </p>
              </CardContent>
            </Card>
            <Card className="border-gray-200 rounded-3xl bg-apple-light/50 border-none shadow-sm card-hover-premium" data-id="element-489" data-mp="3vkp360txq9fm">
              <CardContent className="p-10" data-id="element-490" data-mp="4ew1ha6vw30f">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md mb-6" data-id="element-491" data-mp="4durl91ltmka">
                  <ZapIcon className="w-7 h-7 text-apple-blue" data-id="element-492" data-mp="8dwhkkxz8rjq" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-493" data-mp="dflwwcs8frdm">
                  Rýchlosť
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-494" data-mp="1xuqngg5w3xeu">
                  Kľúčové funkcie dodávame v 14-dňových cykloch. Odozva pod 50
                  milisekúnd znamená, že všetko reaguje okamžite — bez čakania,
                  bez zasekávania.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 5. MARKET ANALYSIS CALLOUT */}
        <div className="reveal max-w-4xl mx-auto text-center mb-28" data-id="element-495" data-mp="2gpfhw8w0mpfr">
          <h4 className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-6" data-id="element-496" data-mp="2cmsuck6dnqod">
            Na čom práve pracujeme?
          </h4>
          <p className="text-2xl md:text-3xl font-medium text-apple-black leading-snug" data-id="element-497" data-mp="22wg2s805ax8q">
            Vytvárame aplikácie šité na mieru pre konkrétne odvetvia — od
            zdravotníctva cez logistiku až po právo. Nie všeobecné nástroje, ale
            riešenia, ktoré presne rozumejú vášmu svetu a vašim potrebám.
          </p>
        </div>

        {/* 6. GRID OF 6 PRIMARY NODE CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-28" data-id="element-498" data-mp="zwgzph0xjthx">
          {nodeCards.map((node, index) => <div key={index} className={`reveal reveal-delay-${Math.min(index + 1, 5)}`} data-id="element-499" data-mp="1dxjkzrk3ozrs" data-mpi={index}>
              <Card className="border-gray-200 rounded-3xl overflow-hidden group card-hover-premium h-full" data-id="element-500" data-mp="19wr9ssro4ksv">
                <div className="h-44 relative overflow-hidden" data-id="element-501" data-mp="1iv33j3ntzypj">
                  <img src={node.image} alt={node.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-502" data-mp="1iut9n4zo17ek" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${node.gradient} opacity-60`} data-id="element-503" data-mp="26knn9ort6zzv"></div>
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md" data-id="element-504" data-mp="21mvs8lvvaxrg">
                    {node.icon}
                  </div>
                </div>
                <CardContent className="p-8" data-id="element-505" data-mp="2q4cyoi5lda5a">
                  <h5 className="text-xl font-bold text-apple-black mb-3" data-id="element-506" data-mp="2c7g01uj8eodu">
                    {node.title}
                  </h5>
                  <p className="text-apple-gray text-sm leading-relaxed font-light" data-id="element-507" data-mp="2g6otsze9um9e">
                    {node.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {/* 7. COMPACT LIST OF ITEMS 07-20 */}
        <div className="reveal max-w-4xl mx-auto mb-36" data-id="element-508" data-mp="1sk06fy6hu1le">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4" data-id="element-509" data-mp="1xhiihxft4wxm">
            {compactItems.map((item, index) => <div key={index} className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0 sm:last:border-b sm:[&:nth-last-child(-n+2)]:border-0 hover:bg-gray-50/50 rounded-lg px-2 -mx-2 transition-colors" data-id="element-510" data-mp="1jj20hachvk06" data-mpi={index}>
                <CheckCircle2Icon className="w-4 h-4 text-apple-blue/40 shrink-0" data-id="element-511" data-mp="3lfszcw4yupmw" />
                <span className="text-sm font-medium text-apple-gray" data-id="element-512" data-mp="3ggg35ks7g67n">
                  {item}
                </span>
              </div>)}
          </div>
        </div>

        {/* NEW CTA BLOCK */}
        <div id="kontakt" className="reveal-scale max-w-4xl mx-auto text-center mb-36 bg-gradient-to-br from-apple-light via-white to-apple-light rounded-[3rem] p-14 md:p-24 border border-gray-200 shadow-lg" data-id="element-513" data-mp="czhfz1tgw1oy">
          <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black mb-8" data-id="element-514" data-mp="3xq9vvv0lsuk">
            Postavíme Váš Globálny Hit?
          </h3>
          <p className="text-xl text-apple-gray leading-relaxed max-w-2xl mx-auto mb-12 font-light" data-id="element-515" data-mp="2opa7phnfiwop">
            Tento zoznam je len začiatok. Máme technológiu, máme tím a máme
            víziu. Hľadáme partnerov, ktorí chcú ovládnuť trh pomocou
            multimodálnej AI.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-516" data-mp="14797isokadj7">
            <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto btn-hover-premium !border-none" data-id="element-517" data-mp="144veepdfzbea">
              Začať Konzultáciu
            </Button>
            <Button size="large" className="!bg-white hover:!bg-gray-50 !text-apple-black !border-2 !border-gray-200 !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto card-hover-premium" data-id="element-518" data-mp="1hyebu82vjtzv">
              Naše Portfólio
            </Button>
          </div>
        </div>

        {/* 8. INTERACTIVE TERMINAL */}
        <div className="reveal-scale" data-id="element-519" data-mp="3tooynh7rix5y">
          <InteractiveTerminal data-id="element-520" data-mp="3tprrvvsk5bp1" />
        </div>

        {/* 9. MARKET POTENTIAL STAT */}
        <div className="reveal text-center pb-16" data-id="element-521" data-mp="1l60hnz7l1te">
          <p className="text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-6" data-id="element-522" data-mp="32fltnqwpij12">
            Veľkosť Príležitosti
          </p>
          <div className="text-7xl md:text-9xl font-extrabold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-apple-black to-gray-400" data-id="element-523" data-mp="2yd8ph6xlyt6e">
            $12.4B
          </div>
        </div>
      </div>
    </section>;
}